import { useCallback, useEffect, useRef, useState } from 'react';
import { resolveSignedDisplayUrl, isLocalOnlyUrl } from '@/utils/imageUrlUtils';
import { pickImageSignOptions } from '@/utils/imageDisplayAccess';
import { resolveImageDisplayByteSize } from '@/pages/ipe/workbench/utils/imageSpaceStorageStats';

/** 图片空间预览弹窗：缩放、拖拽平移、滚轮与远端文件大小补全 */
export function useWorkbenchImagePreview() {
  const [previewImage, setPreviewImage] = useState(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [previewOffset, setPreviewOffset] = useState({ x: 0, y: 0 });
  const [isPreviewDragging, setIsPreviewDragging] = useState(false);
  const previewContainerRef = useRef(null);
  const previewDragRef = useRef(null);

  const resetPreviewView = useCallback(() => {
    // 已是默认视图时保持同一引用，避免无意义 setState 触发父组件连锁重渲染
    setPreviewScale((prev) => (prev === 1 ? prev : 1));
    setPreviewOffset((prev) => (prev?.x === 0 && prev?.y === 0 ? prev : { x: 0, y: 0 }));
    setIsPreviewDragging((prev) => (prev === false ? prev : false));
    previewDragRef.current = null;
  }, []);

  const closePreview = useCallback(() => {
    setPreviewImage((prev) => (prev == null ? prev : null));
    resetPreviewView();
  }, [resetPreviewView]);

  const handlePreviewWheel = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setPreviewScale((prevScale) => Math.max(0.1, Math.min(5, prevScale + delta)));
  }, []);

  useEffect(() => {
    if (!previewImage || !previewContainerRef.current) return undefined;
    const container = previewContainerRef.current;
    container.addEventListener('wheel', handlePreviewWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handlePreviewWheel);
    };
  }, [previewImage, handlePreviewWheel]);

  const previewImageId = previewImage?.id;
  const previewImageRef = useRef(previewImage);
  previewImageRef.current = previewImage;

  useEffect(() => {
    resetPreviewView();
  }, [previewImageId, resetPreviewView]);

  useEffect(() => {
    const image = previewImageRef.current;
    if (!image) return undefined;
    const currentId = image.id;
    const resolved = resolveImageDisplayByteSize(image);
    if (resolved) {
      if (Number(image.size) !== resolved) {
        setPreviewImage((prev) => (
          prev && String(prev.id) === String(currentId)
            ? { ...prev, size: resolved }
            : prev
        ));
      }
      return undefined;
    }

    const pathUrl = typeof image.url === 'string' ? image.url.trim() : '';
    const srcUrl = typeof image.src === 'string' ? image.src.trim() : '';
    const candidate =
      pathUrl && !pathUrl.startsWith('blob:') && !pathUrl.startsWith('data:')
        ? pathUrl
        : srcUrl && !srcUrl.startsWith('blob:') && !srcUrl.startsWith('data:')
          ? srcUrl
          : srcUrl.startsWith('blob:')
            ? srcUrl
            : '';
    if (!candidate || isLocalOnlyUrl(candidate)) return undefined;

    let cancelled = false;
    const signOptions = pickImageSignOptions(image);
    (async () => {
      try {
        const full = candidate.startsWith('blob:')
          ? candidate
          : (await resolveSignedDisplayUrl(candidate, signOptions) || candidate);
        const headRes = await fetch(full, { method: 'HEAD', mode: 'cors' });
        const headLen = headRes.headers.get('Content-Length');
        if (!cancelled && headLen) {
          const headSize = Number(headLen);
          if (Number.isFinite(headSize) && headSize > 0) {
            setPreviewImage((prev) => (
              prev && String(prev.id) === String(currentId)
                ? { ...prev, size: headSize }
                : prev
            ));
            return;
          }
        }

        const getRes = await fetch(full, { method: 'GET', mode: 'cors' });
        if (!getRes.ok) return;
        const blob = await getRes.blob();
        if (cancelled || !blob?.size) return;
        setPreviewImage((prev) => (
          prev && String(prev.id) === String(currentId)
            ? { ...prev, size: blob.size }
            : prev
        ));
      } catch {
        /* 跨域或未返回 Content-Length 时忽略 */
      }
    })();
    return () => {
      cancelled = true;
    };
    // 仅随预览图身份变化拉大小；勿依赖整个 previewImage（改 size 会换对象导致死循环）
  }, [previewImageId]);

  const handlePreviewPointerDown = useCallback((event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    previewDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: previewOffset.x,
      originY: previewOffset.y,
    };
    setIsPreviewDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [previewOffset.x, previewOffset.y]);

  const handlePreviewPointerMove = useCallback((event) => {
    const drag = previewDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    setPreviewOffset({
      x: drag.originX + (event.clientX - drag.startX),
      y: drag.originY + (event.clientY - drag.startY),
    });
  }, []);

  const handlePreviewPointerUp = useCallback((event) => {
    const drag = previewDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    previewDragRef.current = null;
    setIsPreviewDragging(false);
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  return {
    previewImage,
    setPreviewImage,
    previewScale,
    setPreviewScale,
    previewOffset,
    isPreviewDragging,
    previewContainerRef,
    closePreview,
    resetPreviewView,
    handlePreviewPointerDown,
    handlePreviewPointerMove,
    handlePreviewPointerUp,
  };
}
